import React, { useState, useEffect } from 'react';
import {
  Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, Grid, InputLabel, MenuItem, Paper, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography
} from '@mui/material';
import { Apartment, People, Receipt, AttachMoney } from '@mui/icons-material';
import { getApartments, updateApartmentPlan, USE_MOCK_DATA } from '../services/api';
import { mockApartments } from '../services/mockData';

export default function SuperAdminDashboard() {
  const [apartments, setApartments] = useState([]);
  const [openPlan, setOpenPlan] = useState(false);
  const [selectedApt, setSelectedApt] = useState(null);
  const [planForm, setPlanForm] = useState({ planType: 'Free', planExpiryDate: '' });

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setApartments(mockApartments);
      return;
    }
    loadData();
  }, []);

  const loadData = () => {
    getApartments().then(res => setApartments(res.data)).catch(() => {});
  };

  const handlePlanClick = (apt) => {
    setSelectedApt(apt);
    setPlanForm({
      planType: apt.planType || 'Free',
      planExpiryDate: apt.planExpiryDate ? apt.planExpiryDate.split('T')[0] : ''
    });
    setOpenPlan(true);
  };

  const handlePlanUpdate = async () => {
    await updateApartmentPlan(
      selectedApt.id,
      planForm.planType,
      planForm.planExpiryDate || null
    );
    setOpenPlan(false);
    loadData();
  };

  const totalApartments = apartments.length;
  const paidCount = apartments.filter(a => a.planType === 'Paid').length;
  const freeCount = apartments.filter(a => a.planType !== 'Paid').length;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Super Admin Dashboard</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Platform-wide overview and plan management
      </Typography>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e3f2fd' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Apartment sx={{ fontSize: 40, color: '#1976d2' }} />
              <Box>
                <Typography color="text.secondary" variant="body2">Total Apartments</Typography>
                <Typography variant="h4">{totalApartments}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <AttachMoney sx={{ fontSize: 40, color: '#388e3c' }} />
              <Box>
                <Typography color="text.secondary" variant="body2">Paid Plans</Typography>
                <Typography variant="h4">{paidCount}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#fff3e0' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <People sx={{ fontSize: 40, color: '#f57c00' }} />
              <Box>
                <Typography color="text.secondary" variant="body2">Free Plans</Typography>
                <Typography variant="h4">{freeCount}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#f3e5f5' }}>
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Receipt sx={{ fontSize: 40, color: '#7b1fa2' }} />
              <Box>
                <Typography color="text.secondary" variant="body2">Active</Typography>
                <Typography variant="h4">{apartments.filter(a => a.isActive).length}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Apartments with Plan Management */}
      <Typography variant="h6" gutterBottom>All Apartments - Plan Management</Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Apartment</TableCell>
              <TableCell>Address</TableCell>
              <TableCell align="center">Flats</TableCell>
              <TableCell align="center">Plan</TableCell>
              <TableCell align="center">Expiry</TableCell>
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {apartments.map(apt => (
              <TableRow key={apt.id}>
                <TableCell><strong>{apt.name}</strong></TableCell>
                <TableCell>{apt.address}</TableCell>
                <TableCell align="center">{apt.totalFlats}</TableCell>
                <TableCell align="center">
                  <Chip
                    label={apt.planType || 'Free'}
                    color={apt.planType === 'Paid' ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  {apt.planExpiryDate
                    ? new Date(apt.planExpiryDate).toLocaleDateString()
                    : '—'}
                </TableCell>
                <TableCell align="center">
                  <Button size="small" variant="outlined" onClick={() => handlePlanClick(apt)}>
                    {apt.planType === 'Paid' ? 'Manage' : 'Upgrade'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Plan Update Dialog */}
      <Dialog open={openPlan} onClose={() => setOpenPlan(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Plan - {selectedApt?.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Plan Type</InputLabel>
            <Select value={planForm.planType} label="Plan Type" onChange={(e) => setPlanForm({ ...planForm, planType: e.target.value })}>
              <MenuItem value="Free">Free</MenuItem>
              <MenuItem value="Paid">Paid</MenuItem>
            </Select>
          </FormControl>
          {planForm.planType === 'Paid' && (
            <TextField
              fullWidth label="Plan Expiry Date" type="date" margin="normal"
              InputLabelProps={{ shrink: true }}
              value={planForm.planExpiryDate}
              onChange={(e) => setPlanForm({ ...planForm, planExpiryDate: e.target.value })}
              helperText="Leave empty for no expiry"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlan(false)}>Cancel</Button>
          <Button onClick={handlePlanUpdate} variant="contained">Update Plan</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
